import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField, useTokens } from '@vibesites/runtime/react';
import { EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio } from '../utils/luminance';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function ApplyNowActions() {
  const applyUrl =
    (usePropertyField('property_apply_now_url') as string) ||
    (usePropertyField('property_online_leasing_url') as string) ||
    '';
  const helpCtaLabel = useContentToken('applynowactions.help_cta_label');
  const phone = helpCtaLabel || '(555) 555-5555';

  const applyTitle = useContentToken('applynowactions.apply_title');
  const applyCta = useContentToken('applynowactions.apply_cta_label');
  const helpTitle = useContentToken('applynowactions.help_title');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  // Match the canonical "CTA over white" treatment used elsewhere in the
  // theme (PropertyOverview): outline button that fills with the accent on
  // hover. When the property's primary fails WCAG AA contrast against the
  // white card background, fall back to the dark text token so the CTA
  // stays legible — same luminance picker the overview/header CTAs use.
  const tokens = useTokens();
  const [ctaAccent, ctaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    return contrastRatio(primary, background) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text)', 'var(--vibe-color-background)'] as const;
  }, [tokens]);

  return (
    <section
      className="ana-section"
      style={{
        ['--rise-ana-cta-accent' as string]: ctaAccent,
        ['--rise-ana-cta-on-accent' as string]: ctaOnAccent,
      }}
    >
      <div ref={sectionRef} className="reveal-fade-up ana-container">
        <EditableToken path="applynowactions.heading" as="h2" className="sr-only">
          Apply Now
        </EditableToken>
        <div className="ana-grid">
          {/* Apply Online Card */}
          <div className="ana-card">
            <div className="ana-card-icon-wrap ana-card-icon--apply">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
              </svg>
            </div>
            <EditableToken path="applynowactions.apply_title" as="h3" className="ana-card-title">
              {applyTitle || 'Apply Online'}
            </EditableToken>
            <RichTextContent path="applynowactions.apply_description" as="p" className="ana-card-desc" />
            {applyUrl ? (
              <a href={applyUrl} target="_blank" rel="noopener noreferrer" className="ana-btn">
                <span className="ana-btn-label">
                  <EditableToken path="applynowactions.apply_cta_label" as="span">
                    {applyCta || 'Start Your Application'}
                  </EditableToken>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
              </a>
            ) : (
              <span className="ana-btn ana-btn--disabled">
                <span className="ana-btn-label">Application Link Not Available</span>
              </span>
            )}
          </div>

          {/* Need Help Card */}
          <div className="ana-card">
            <div className="ana-card-icon-wrap ana-card-icon--help">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" fill="currentColor"/>
              </svg>
            </div>
            <EditableToken path="applynowactions.help_title" as="h3" className="ana-card-title">
              {helpTitle || 'Questions? Call Us'}
            </EditableToken>
            <RichTextContent path="applynowactions.help_description" as="p" className="ana-card-desc" />
            <a
              href={phone ? `tel:${phone}` : '#'}
              className="dniphone dniphonehref ana-btn"
            >
              <span className="ana-btn-label">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4.64 1.33A.67.67 0 015.33 1H7a.67.67 0 01.64.47l.53 1.6a.67.67 0 01-.17.69L6.88 4.88a8 8 0 003.24 3.24l1.12-1.12a.67.67 0 01.69-.17l1.6.53A.67.67 0 0114 8v1.67a.67.67 0 01-.67.66A10 10 0 013.33 1.67.67.67 0 014 1h.64z" fill="currentColor"/></svg>
                <EditableToken path="applynowactions.help_cta_label" as="span">
                  {formatPhone(phone)}
                </EditableToken>
              </span>
            </a>
          </div>
        </div>
      </div>

      <style>{`
        .ana-section {
          padding: 2rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .ana-section { padding: 2.5rem 0 4rem; }
        }
        .ana-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .ana-container { padding: 0 1.5rem; } }

        .ana-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .ana-grid { grid-template-columns: 1fr 1fr; }
        }

        .ana-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2.5rem 2rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .ana-card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .ana-card-icon-wrap {
          width: 56px; height: 56px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 14px;
          margin-bottom: 1.25rem;
        }
        .ana-card-icon--apply {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }
        .ana-card-icon--help {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }

        .ana-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0 0 0.75rem;
        }
        .ana-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          margin: 0 0 1.5rem;
          max-width: 22rem;
        }

        /* Outline CTA matching the canonical "over white" treatment used in
           PropertyOverview / PetPolicy: transparent body, accent border +
           label, fills with the accent on hover. The accent is luminance-
           picked (--rise-ana-cta-accent) so pale primaries fall back to text. */
        .ana-btn {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.875rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          cursor: pointer;
          background: transparent;
          border: 1.5px solid var(--rise-ana-cta-accent, var(--vibe-color-primary));
          color: var(--rise-ana-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
          transition: color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
          margin-top: auto;
        }
        .ana-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--rise-ana-cta-accent, var(--vibe-color-primary));
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 0;
        }
        .ana-btn-label {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .ana-btn:hover {
          color: var(--rise-ana-cta-on-accent, var(--vibe-color-text-on-primary));
          transform: translateY(-2px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--rise-ana-cta-accent, var(--vibe-color-primary)) 30%, transparent);
        }
        .ana-btn:hover::before { opacity: 1; }
        .ana-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        .ana-btn--disabled,
        .ana-btn--disabled:hover {
          background: transparent;
          color: var(--vibe-color-text-muted);
          border-color: var(--vibe-color-border, #e5e7eb);
          cursor: default;
          opacity: 0.7;
          transform: none;
          box-shadow: none;
        }
        .ana-btn--disabled::before { display: none; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApplyNowActions',
  displayName: 'Apply Now Actions',
  tokens: {
    'applynowactions.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Apply Now',
    },
    'applynowactions.apply_title': {
      label: 'Apply Card Title',
      type: 'text',
      defaultValue: 'Apply Online',
    },
    'applynowactions.apply_description': {
      label: 'Apply Card Description',
      type: 'richtext',
      defaultValue:
        '<p>Ready to make this community your home? Start your application online in minutes. Our secure application makes it easy to apply, submit documents, and track your status.</p>',
    },
    'applynowactions.apply_cta_label': {
      label: 'Apply CTA Label',
      type: 'text',
      defaultValue: 'Start Your Application',
    },
    'applynowactions.help_title': {
      label: 'Help Card Title',
      type: 'text',
      defaultValue: 'Questions? Call Us',
    },
    'applynowactions.help_description': {
      label: 'Help Card Description',
      type: 'richtext',
      defaultValue:
        '<p>Not sure where to start or have questions about the application process, qualifications, or required documents? Our leasing team is happy to help.</p>',
    },
    'applynowactions.help_cta_label': {
      label: 'Phone CTA Label',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
  },
};

(ApplyNowActions as any).contentSchema = contentSchema;

export default ApplyNowActions;
